import { useParams } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { SplitScreenBackgroundVertical } from "../components/Background";
import { TextLargeInput, FileInputExtended } from "../components/Input-Components";
import { ChatViewer } from "../components/MessageComponents";
import { formatTimestamp } from "../utils";
import { AccountContext } from "../contexts/AccountContexts";
import { sendMessage, sendMessagesRead, sendMessageWithFileLinks } from "../utils/messagingUtils";
import { NotificationContext } from "../contexts/NotificationContexts";
import { ContentContext } from "../contexts/ContentContext";
import { requestTicketTemp } from "../requests/ticket_requests";
import { requestPulseCheck } from "../requests/users_requests";
import { jwtDecode } from "jwt-decode";
import { PageHeader } from "../components/HeaderComponents";
import { useTranslation } from "react-i18next";
import NavBar from "../components/NavBar";
import { MAX_FILE_SIZE, MAX_FILES } from "../entries";
import { ConnectionLostMessage } from "../components/ModalComponents";
import { MdClose } from "react-icons/md";


const defaultTicket = {
    id: null,
    name: "",
    email: "",
    category: null,
    server: null,
    characterName: "",
    subject: "",
    description: "",
    status: "",
    createdAt: null,
    lastUpdated: null,
    attachments: [],
    priority: "Normal",
    chat_id: null,
    notes_id: null,
    assignedTo: null,

}


function UserTicket() {
    const { t } = useTranslation(["edit", "common"])
    const { user, setUser, setIsTempUser, language } = useContext(AccountContext)
    const { socket, initializeSocket, reconnected } = useContext(NotificationContext)
    const { categories, deletedCategories, servers, openErrorModal } = useContext(ContentContext)
    const { ticket_info } = useParams();

    const [ticketInfo, setTicketInfo] = useState(defaultTicket)
    // const [newMessage, setNewMessage] = useState("");
    // const [newFiles, setNewFiles] = useState([]);
    const [anonUser, setAnonUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [replyTo, setReplyTo] = useState(null)


    useEffect(() => {

        if (anonUser) {

            socket.emit('join_chatroom', { room_id: ticketInfo.chat_id, name: anonUser.id })
            sendMessagesRead({ socket: socket, userId: anonUser.id, chatId: ticketInfo.chat_id })


            socket.on('ticket_assigned', (pckg) => {
                if (pckg.ticket.id !== ticketInfo.id) {
                    return
                }
                setTicketInformation({ ticketData: pckg.ticket })
            })

            // Cleanup socket connection
            return () => {
                socket.emit('leave_chatroom', { room_id: ticketInfo.chat_id, name: anonUser.id });
                socket.off('new_message');
            }
        }
    }, [loading, reconnected]);

    const setTicketInformation = ({ ticketData }) => {
        setTicketInfo(ticketData)
    }

    useEffect(() => {
        // Upon opening the page decode the ticket_info and make a ticket info request
        const decoded = JSON.parse(atob(ticket_info));

        const getUserFromIdToken = (idToken) => {
            const userData = jwtDecode(idToken)
            setUser(userData)
        }
        if (user && user.type !== "temp") {
            return
        }
        requestTicketTemp({ ticketId: decoded.ticketId, ticketEmail: decoded.email }).then((data) => {
            setTicketInformation({ ticketData: data.ticket })
            setAnonUser(data.user);
            requestPulseCheck({}).then((data) => {
                setIsTempUser(!!data.temp)
                getUserFromIdToken(data.id_token)
                initializeSocket()
                if (!user) {
                    window.location.href = window.location.pathname;
                }
            })
                .catch((error) => {
                    console.error(error.status, error.message)
                    openErrorModal(error)
                })
        })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }, [])


    useEffect(() => {
        if (user?.class === "user" && user?.type === "normal") {
            window.location.href = "/"
        }
    }, [user])


    const handleSendMessage = (message, files) => {


        if (files.length > 0) {
            sendMessageWithFileLinks({ socket: socket, message: message, files: files, ticketInfo: ticketInfo, user: anonUser, replyTo: replyTo?.type === "message" ? replyTo : null })
        }
        else {
            sendMessage({ socket: socket, message: message, ticketInfo: ticketInfo, user: anonUser, replyTo: replyTo?.type === "message" ? replyTo : null })
        }

    }

    if (anonUser === null || user === null) {
        return <></>
    }
    else {
        if (loading) {
            setLoading(false)
        }
    }

    function userTicketStatus(ticketStatus) {
        if (ticketStatus !== "CLOSED" && ticketStatus !== "RESOLVED") {
            return "ACTIVE"
        }
        else {
            return ticketStatus
        }
    }


    return <>
        <SplitScreenBackgroundVertical />
        <NavBar />
        <ConnectionLostMessage />
        <PageHeader pageTitle={t("Ticket") + " - " + ticketInfo.createdAt} />
        <main className="flex flex-col w-full mx-auto my-auto top-0 bottom-0 left-0 right-0 max-w-[1200px] py-3 sm:p-5 gap-4">
            <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 sm:p-5 rounded-xl shadow-2xl gap-4 pb-24">
                <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 p-3 rounded-xl shadow-2xl gap-3 py-5">
                    <h1 className="w-full text-center text-lg font-semibold">{t('Ticket Info')}</h1>
                    <div className="flex flex-col sm:flex-row w-full">
                        <div className="flex flex-col w-full gap-3">
                            <h1 className="text-gray-800 dark:text-gray-300 font-semibold">
                                {t('Ticket')} - {ticketInfo.createdAt}
                            </h1>
                            <div className="flex flex-col">
                                <span className="font-semibold">{ticketInfo.name}</span>
                                <span className="">{ticketInfo.email}</span>
                            </div>

                            <div className="flex flex-col">
                                <span>{t('Category')} : <span className="font-semibold">{categories[ticketInfo.category] ? (categories[ticketInfo.category]?.locales?.[language] ? categories[ticketInfo.category]?.locales[language] : t(categories[ticketInfo.category].title, { ns: "common" })) : deletedCategories[ticketInfo.category] ? t(deletedCategories[ticketInfo.category].title, { ns: "common" }) : "UNK"}</span></span>
                                <span className="truncate">{t('Character Name')} : <span className="font-semibold">{ticketInfo.characterName}</span></span>
                                {
                                    ticketInfo.violatersName && ticketInfo.violatersName !== "" &&
                                    <span>{t("Violater's Name")} : <span className="font-semibold">{ticketInfo.violatersName}</span></span>
                                }
                                <span>{t('Server')} : <span className="font-semibold">{servers[ticketInfo.server].title ? servers[ticketInfo.server].title : servers[ticketInfo.server].name}</span></span>
                            </div>

                        </div>

                        <div className="flex flex-col gap-3 max-w-max w-max">
                            <span className="flex gap-1 w-max">
                                {t('Status')} : <span className="font-semibold">{t(userTicketStatus(ticketInfo.status))}</span>
                            </span>

                            <a className="flex gap-1 w-max">
                                {t('Created At')} : <span className="font-semibold">{formatTimestamp(ticketInfo.createdAt * 1000)}</span>
                            </a>

                        </div>
                    </div>

                </div>

                <ChatViewer key={ticketInfo.chat_id} socket={socket} ticketInfo={ticketInfo} chatId={ticketInfo.chat_id} firstPerson={user} anonUser={anonUser} setAnonUser={setAnonUser} setTicketInformation={setTicketInformation} replyTo={replyTo} setReplyTo={setReplyTo} />
                {
                    (ticketInfo.status !== "CLOSED" && ticketInfo.status !== "RESOLVED") &&
                    <UserMessageWritingComponent ticketInfo={ticketInfo} handleSendMessage={handleSendMessage} replyTo={replyTo} setReplyTo={setReplyTo} />
                }

            </div>
        </main>
    </>
}


export const UserMessageWritingComponent = ({ ticketInfo, handleSendMessage, handleResolveTicket, replyTo, setReplyTo }) => {
    const { t } = useTranslation(['edit', 'errors'])

    const { user } = useContext(AccountContext)
    const { openErrorModal } = useContext(ContentContext)
    const [newMessage, setNewMessage] = useState("")
    const [newFiles, setNewFiles] = useState([])

    const _setFiles = (f) => {
        f = f.filter((el) => {
            for (let i = 0; i < newFiles.length; i++) {
                if (newFiles[i].name === el.name && newFiles[i].lastModified === el.lastModified) {
                    return false
                }
            }
            return true
        })
        const _files = [...f, ...newFiles].slice(0, MAX_FILES)
        for (const file of _files) {
            if (file.size > MAX_FILE_SIZE) {
                const error = {
                    status: t('WARNING', { ns: "errors" }),
                    message: t('file_size_exceeded', { ns: "errors" })
                }
                openErrorModal(error)
                setNewFiles([])
                return
            }
        }
        setNewFiles(_files)
    }
    const submitCondition = ticketInfo.status === "CLOSED" || ticketInfo.status === "RESOLVED" || newMessage.trim() === ""

    const onSendMessage = () => {
        if (submitCondition) {
            return
        }
        handleSendMessage(newMessage, newFiles)
        setNewMessage("")
        setNewFiles([]);
    }


    const onTicketResolved = () => {
        handleResolveTicket()
    }


    return (
        <div className="flex flex-col w-full bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-300 sm:px-3 rounded-xl shadow-2xl gap-3 pb-20 sm:pt-5 ">
            <div className="px-3">{replyTo && t("Replying to") + ":"} {replyTo && (
                <div className="bg-gray-200 dark:bg-gray-600 p-2 rounded-md mb-2 flex justify-between items-center">
                    <span><strong>{replyTo.sender_name}</strong>: {replyTo.message}</span>
                    <MdClose 
                        className="font-bold text-2xl bg-white rounded-full text-gray-500 cursor-pointer hover:scale-110 transition-all duration-200 ease-out" 
                        width={20}
                        onClick={() => setReplyTo(null)}
                    />
                </div>
            )}
            </div>
            <TextLargeInput
                labelName={t("Message")}
                placeHolder={t("Enter your reply...")}
                inputVariable={newMessage}
                inputSetVariable={setNewMessage}
                limit={500}
                isChat={true}
            />
            <div className="w-full">
                <div className="flex md:flex-auto md:flex-grow-0 md:flex-shrink-0 md:w-1/2 w-full px-2">
                    <p className="font-semibold px-1 text-sm sm:text-md">
                        {t('Attachments')} <small>({t('You can select multiple files')} ({t('max. 5')}))</small>
                    </p>
                </div>
                <FileInputExtended inputVariable={newFiles} inputSetVariable={_setFiles} />
            </div>
            <div className="flex justify-center w-full">

                <div className="flex justify-center gap-5 sm:gap-3 w-full mt-2 md:mt-0 flex-col sm:flex-row max-w-max">
                    {
                        user?.type !== "temp" && handleResolveTicket && ticketInfo?.status !== "NEW TICKET" &&
                        <button
                            disabled={ticketInfo.status === "CLOSED" || ticketInfo.status === "RESOLVED"}
                            className="btn bg-green-800 order-2 sm:order-1 hover:bg-green-800/90 border-gray-300 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-white font-semibold"
                            onClick={onTicketResolved}
                        >
                            {t('My problem is solved')}
                        </button>
                    }
                    <button
                        disabled={submitCondition}
                        className="btn bg-primary order-1 sm:order-2 hover:bg-primary/90 border-gray-300 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-white font-semibold"
                        onClick={onSendMessage}
                    >
                        {t('Submit')}
                    </button>
                </div>
            </div>

        </div>
    )
}


export default UserTicket;